// helpers/login-error-handler.js

export const parseApiError = (error) => {
    if (!error) return "An unknown error occurred.";

    // 1. Handle Axios Response Errors (4xx, 5xx)
    if (error.response && error.response.data) {
        const data = error.response.data;
        const statusCode = error.response.status;
        const message = data.statusMessage || data.StatusMessage;
        if (message) return message;
        if (statusCode === 500) {
            return 'We ran into technical problems. Please contact support@sweldonow.ph';
        }
        if (data.errors) return Object.values(data.errors).flat().join(', ');
        if (typeof data === 'string') return data;
    }

    // 2. Handle Browser/WebAuthn Specific Errors
    if (error.name === 'NotAllowedError') {
        // Check if the error is actually due to local TLS/SSL certificate issues
        if (error.message && error.message.includes("TLS certificate errors")) {
            return "Biometric login failed because this site's security certificate is invalid.";
        }
        // Fallback default message when a user manually hits the "Cancel" button
        return "Operation cancelled or timed out.";
    }
    
    if (error.name === 'NotSupportedError') return "Your device does not support this biometric method.";
    if (error.name === 'InvalidStateError') return "This device is already registered.";

    // 3. Handle Network Errors
    if (error.message === 'Network Error') return "Unable to connect to the server. Please check your internet.";

    // 4. Default Fallback
    return error.message || "An unknown error occurred.";
};

/**
 * Generates the precise HTML body for your AwsSesEmailRequest payload
 */
export const generateErrorHtmlReport = (error, contextData = {}) => {
    const timestamp = new Date().toLocaleString();
    const url = typeof window !== 'undefined' ? window.location.href : 'Unknown';
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown';
    const errorName = error && error.name ? error.name : 'RuntimeError';
    const errorMessage = error && error.message ? error.message : 'No message available';
    
    let stackTrace = error && error.stack ? error.stack : 'No stack trace available';
    if (stackTrace.includes("Generated trace context")) {
        stackTrace = stackTrace.replace("Error: Generated trace context", "Captured Execution Frame Trace:");
    }

    let networkInfoHtml = 'No network details available.';
    if (error && error.response) {
        networkInfoHtml = `
            <strong>Type:</strong> Response Error (Server replied with status ${error.response.status})<br/>
            <strong>Payload Data:</strong> <pre style="background:#f4f4f4; padding:8px;">${JSON.stringify(error.response.data, null, 2)}</pre>
        `;
    } else if (error && error.request) {
        networkInfoHtml = `<strong>Type:</strong> Request Error (Server hit a timeout or dropped the connection before replying)`;
    } else {
        networkInfoHtml = `<strong>Type:</strong> Client-side JavaScript / WebAuthn Engine Failure`;
    }

    return `
        <div style="font-family: Arial, sans-serif; color: #333; max-width: 600px; margin: 0 auto; border: 1px solid #ddd; padding: 20px; border-radius: 8px;">
            <h2 style="color: #d9534f; margin-top: 0;">🚨 SweldoNow UI Exception Detected</h2>
            <p>An automated diagnostic error report has been captured during a biometric device login attempt.</p>
            
            <table style="width: 100%; border-collapse: collapse; margin-top: 15px;">
                <tr style="background-color: #f9f9f9;">
                    <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #ddd; width: 35%;">Timestamp</td>
                    <td style="padding: 8px; border-bottom: 1px solid #ddd;">${timestamp}</td>
                </tr>
                <tr>
                    <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #ddd;">Target URL</td>
                    <td style="padding: 8px; border-bottom: 1px solid #ddd; color: #0066cc;">${url}</td>
                </tr>
                <tr>
                    <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #ddd;">UserId Encrypted</td>
                    <td style="padding: 8px; border-bottom: 1px solid #ddd;"><code>${contextData.userId || 'N/A'}</code></td>
                </tr>
                <!-- 🚨 NEW USER IDENTIFICATION ROWS END HERE -->
                <tr style="background-color: #f9f9f9;">
                    <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #ddd;">User Agent Fingerprint</td>
                    <td style="padding: 8px; border-bottom: 1px solid #ddd;"><code>${contextData.fingerprint || 'N/A'}</code></td>
                </tr>
                <tr>
                    <td style="padding: 8px; font-weight: bold; border-bottom: 1px solid #ddd;">Device Supported / Exists</td>
                    <td style="padding: 8px; border-bottom: 1px solid #ddd;">Supported: ${contextData.deviceLoginSupported} | Exists: ${contextData.isDeviceLoginCredentialExists}</td>
                </tr>
            </table>

            <h3 style="color: #333; margin-top: 20px; border-bottom: 2px solid #ddd; padding-bottom: 5px;">Error Matrix</h3>
            <p><strong>Class Name:</strong> <span style="background-color: #f0ad4e; color: white; padding: 2px 6px; border-radius: 3px; font-size: 12px;">${errorName}</span></p>
            <p><strong>Message:</strong> <span style="color: #c9302c; font-weight: bold;">${errorMessage}</span></p>
            
            <h4 style="margin-bottom: 5px;">Stack Trace:</h4>
            <pre style="background: #222; color: #fff; padding: 12px; border-radius: 4px; overflow-x: auto; font-size: 11px; font-family: Courier, monospace;">${stackTrace}</pre>

            <h3 style="color: #333; margin-top: 20px; border-bottom: 2px solid #ddd; padding-bottom: 5px;">Network Environment Details</h3>
            <p>${networkInfoHtml}</p>

            <hr style="border: 0; border-top: 1px solid #eee; margin-top: 20px;" />
            <p style="font-size: 11px; color: #777; text-align: center;">This message was generated systematically by SweldoNow Enterprise Client Engine.</p>
        </div>
    `;
};